
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { MapPin, ExternalLink } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface ContactLocationStepProps {
  data: any;
  onChange: (field: string, value: any) => void;
  errors?: Record<string, string>;
}

const ContactLocationStep = ({ data, onChange, errors }: ContactLocationStepProps) => {
  const [locationUrl, setLocationUrl] = useState('');
  const [extracting, setExtracting] = useState(false);

  const expandShortenedUrl = async (url: string): Promise<string> => {
    try {
      // محاولة الحصول على الرابط الكامل من الروابط المختصرة
      const response = await fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`, {
        method: 'HEAD',
        redirect: 'follow'
      });
      
      if (response.url && response.url !== url) {
        console.log('تم توسيع الرابط من:', url, 'إلى:', response.url);
        return response.url;
      }
    } catch (error) {
      console.log('فشل في توسيع الرابط، سيتم المحاولة مع الرابط الأصلي');
    }
    
    return url;
  };

  const extractLocationFromUrl = async () => {
    if (!locationUrl.trim()) {
      toast({
        title: "خطأ",
        description: "يرجى إدخال رابط الموقع",
        variant: "destructive"
      });
      return;
    }

    setExtracting(true);
    try {
      let lat: number | null = null;
      let lng: number | null = null;
      let address = '';

      console.log('استخراج الموقع من الرابط:', locationUrl);

      // محاولة توسيع الرابط إذا كان مختصراً
      let expandedUrl = locationUrl;
      if (locationUrl.includes('goo.gl') || locationUrl.includes('maps.app.goo.gl')) {
        console.log('محاولة توسيع الرابط المختصر...');
        expandedUrl = await expandShortenedUrl(locationUrl);
      }

      console.log('الرابط بعد التوسيع:', expandedUrl);

      // أنماط محسّنة لاستخراج الإحداثيات
      const patterns = [
        // @lat,lng,zoom
        /@(-?\d+\.?\d*),(-?\d+\.?\d*),/,
        // @lat,lng
        /@(-?\d+\.?\d*),(-?\d+\.?\d*)(?:[^,\d]|$)/,
        // ?q=lat,lng
        /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
        // place/lat,lng
        /place\/(-?\d+\.?\d*),(-?\d+\.?\d*)/,
        // dir//lat,lng
        /dir\/\/(-?\d+\.?\d*),(-?\d+\.?\d*)/,
        // ll=lat,lng
        /ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
        // center=lat,lng
        /center=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
        // أي إحداثيات في الرابط
        /(-?\d{1,3}\.\d{4,}),(-?\d{1,3}\.\d{4,})/
      ];

      // البحث عن الإحداثيات باستخدام الأنماط المختلفة
      for (const pattern of patterns) {
        const match = expandedUrl.match(pattern);
        if (match && match[1] && match[2]) {
          const extractedLat = parseFloat(match[1]);
          const extractedLng = parseFloat(match[2]);
          
          // التحقق من صحة الإحداثيات
          if (extractedLat >= -90 && extractedLat <= 90 && 
              extractedLng >= -180 && extractedLng <= 180 &&
              Math.abs(extractedLat) > 0.01 && Math.abs(extractedLng) > 0.01) {
            lat = extractedLat;
            lng = extractedLng;
            console.log('تم العثور على إحداثيات صالحة:', lat, lng);
            break;
          }
        }
      }

      // إذا لم نجد إحداثيات، نحاول استخراج من الرابط الأصلي أيضاً
      if (lat === null || lng === null) {
        console.log('محاولة استخراج من الرابط الأصلي...');
        for (const pattern of patterns) {
          const match = locationUrl.match(pattern);
          if (match && match[1] && match[2]) {
            const extractedLat = parseFloat(match[1]);
            const extractedLng = parseFloat(match[2]);
            
            if (extractedLat >= -90 && extractedLat <= 90 && 
                extractedLng >= -180 && extractedLng <= 180 &&
                Math.abs(extractedLat) > 0.01 && Math.abs(extractedLng) > 0.01) {
              lat = extractedLat;
              lng = extractedLng;
              console.log('تم العثور على إحداثيات من الرابط الأصلي:', lat, lng);
              break;
            }
          }
        }
      }

      // محاولة أخيرة: البحث عن أي رقمين مفصولين بفاصلة
      if (lat === null || lng === null) {
        console.log('محاولة البحث العام عن الإحداثيات...');
        const generalPattern = /(-?\d+\.?\d{4,})[,\s]+(-?\d+\.?\d{4,})/g;
        const matches = [...expandedUrl.matchAll(generalPattern)];
        
        for (const match of matches) {
          const extractedLat = parseFloat(match[1]);
          const extractedLng = parseFloat(match[2]);
          
          if (extractedLat >= -90 && extractedLat <= 90 && 
              extractedLng >= -180 && extractedLng <= 180 &&
              Math.abs(extractedLat) > 0.01 && Math.abs(extractedLng) > 0.01) {
            lat = extractedLat;
            lng = extractedLng;
            console.log('تم العثور على إحداثيات من البحث العام:', lat, lng);
            break;
          }
        }
      }

      if (lat !== null && lng !== null) {
        // تحديث البيانات
        onChange('location_lat', lat);
        onChange('location_lng', lng);
        
        // محاولة استخراج العنوان
        try {
          if (expandedUrl.includes('place/') && expandedUrl.split('place/')[1]) {
            const placePart = expandedUrl.split('place/')[1].split('/')[0];
            const decodedPlace = decodeURIComponent(placePart.replace(/\+/g, ' '));
            address = decodedPlace.replace(/(-?\d+\.?\d*),(-?\d+\.?\d*)/, '').trim();
          }
        } catch (error) {
          console.log('خطأ في استخراج العنوان:', error);
        }
        
        if (!address) {
          address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        }
        
        onChange('address', data.address || address);

        toast({
          title: "تم استخراج الموقع بنجاح",
          description: `الإحداثيات: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        });

        console.log('تم استخراج الموقع بنجاح:', { lat, lng, address });
        setLocationUrl('');
      } else {
        console.log('فشل في استخراج الإحداثيات من الرابط');
        toast({
          title: "خطأ في استخراج الموقع",
          description: "لم يتم العثور على إحداثيات صالحة في الرابط. جرب نسخ الرابط مرة أخرى من خرائط جوجل.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('خطأ في استخراج الموقع:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء معالجة الرابط. جرب مرة أخرى.",
        variant: "destructive"
      });
    } finally {
      setExtracting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-right flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
          التواصل والموقع
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* بيانات التواصل */}
        <div>
          <h3 className="text-lg font-semibold mb-3 text-right">بيانات التواصل</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="contact_phone">رقم الهاتف</Label>
              <Input
                id="contact_phone"
                value={data.contact_phone || ''}
                onChange={(e) => onChange('contact_phone', e.target.value)}
                placeholder="96891234567"
                className="text-right"
              />
            </div>

            <div>
              <Label htmlFor="contact_whatsapp">واتساب</Label>
              <Input
                id="contact_whatsapp"
                value={data.contact_whatsapp || ''}
                onChange={(e) => onChange('contact_whatsapp', e.target.value)}
                placeholder="96891234567"
                className="text-right"
              />
            </div>

            <div>
              <Label htmlFor="contact_email">البريد الإلكتروني</Label>
              <Input
                id="contact_email"
                type="email"
                value={data.contact_email || ''}
                onChange={(e) => onChange('contact_email', e.target.value)}
                placeholder="owner@example.com"
                className="text-right"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* الموقع الجغرافي */}
        <div>
          <h3 className="text-lg font-semibold mb-3 text-right">الموقع الجغرافي</h3>
          
          {/* إدخال رابط الموقع */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="location_url">رابط الموقع من خرائط جوجل</Label>
              <div className="flex gap-2">
                <Input
                  id="location_url"
                  value={locationUrl}
                  onChange={(e) => setLocationUrl(e.target.value)}
                  placeholder="الصق رابط خرائط جوجل هنا..."
                  className="text-right flex-1"
                />
                <Button 
                  onClick={extractLocationFromUrl}
                  disabled={extracting}
                  variant="outline"
                >
                  {extracting ? 'جار الاستخراج...' : 'استخراج الموقع'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1 text-right">
                انسخ أي رابط من خرائط جوجل والصقه هنا - يدعم جميع أنواع الروابط بما في ذلك الروابط المختصرة
              </p>
            </div>

            {/* عرض الإحداثيات المستخرجة */}
            {data.location_lat && data.location_lng && (
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-5 w-5 text-green-600" />
                  <h4 className="font-medium text-green-900 text-right">تم تحديد الموقع بنجاح</h4>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm text-green-800">
                  <div className="text-right">
                    <p><strong>خط العرض:</strong> {data.location_lat.toFixed(6)}</p>
                  </div>
                  <div className="text-right">
                    <p><strong>خط الطول:</strong> {data.location_lng.toFixed(6)}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    const mapsUrl = `https://www.google.com/maps?q=${data.location_lat},${data.location_lng}`;
                    window.open(mapsUrl, '_blank');
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  مشاهدة في خرائط جوجل
                </Button>
              </div>
            )}

            {/* إدخال يدوي للإحداثيات */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-right">أو أدخل الإحداثيات يدوياً</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location_lat">خط العرض</Label>
                  <Input
                    id="location_lat"
                    type="number"
                    step="any"
                    value={data.location_lat || ''}
                    onChange={(e) => onChange('location_lat', parseFloat(e.target.value) || null)}
                    placeholder="23.5859"
                    className="text-right"
                  />
                </div>

                <div>
                  <Label htmlFor="location_lng">خط الطول</Label>
                  <Input
                    id="location_lng"
                    type="number"
                    step="any"
                    value={data.location_lng || ''}
                    onChange={(e) => onChange('location_lng', parseFloat(e.target.value) || null)}
                    placeholder="58.4059"
                    className="text-right"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* نصائح محسّنة */}
          <div className="bg-blue-50 p-3 rounded-lg mt-4">
            <h4 className="font-medium text-blue-900 mb-2 text-right">كيفية الحصول على رابط الموقع:</h4>
            <ol className="text-sm text-blue-800 space-y-1 text-right list-decimal list-inside">
              <li>افتح خرائط جوجل (maps.google.com)</li>
              <li>ابحث عن الموقع أو انقر عليه</li>
              <li>انسخ الرابط من شريط العناوين أو اضغط على "مشاركة"</li>
              <li>الصق الرابط في الحقل أعلاه</li>
            </ol>
            <p className="text-xs text-blue-600 mt-2 text-right">
              ملاحظة: يدعم النظام جميع أنواع روابط خرائط جوجل بما في ذلك الروابط المختصرة (goo.gl)
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactLocationStep;
